import React, { useCallback, useEffect, useRef, useState, VFC } from 'react'
import styled from 'styled-components'

import { ImageV2 } from '@Components/Image'
import { applyTranslateTransistion, applyZoomTransition } from '@Components/Slider/utils/helpers'
import { up } from '@Globals/Style/breakpoints'
import { useTranslation } from '@Globals/Translations'

import { SliderProps } from './Slider.interface'

const ExpandFullWidthHeight = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 100vh;
  width: 100vw;
  -webkit-overflow-scrolling: touch;

  img {
    width: 100%;
    height: auto;

    ${up('tablet')} {
      width: auto;
      height: 100%;
    }
  }
`

const ExitZoom = styled.button`
  display: flex;
  position: fixed;
  top: 4px;
  right: 56px;
  margin-top: 4px;
  z-index: 1001;
  cursor: pointer;
  background: rgba(245, 245, 245, .6) !important;
  padding: 8px 8px;
  margin-left: 8px;
  border: none;

  &:hover {
    background: rgba(180, 180, 180, 0.5);
  }
`

export interface TransformMatrix {
  pointerX: number
  pointerY: number
  scale: number
  xPos: number
  yPos: number
}

type PointerPos = { x: number, y: number }
const SCALE_FACTOR = 3

const initialPointer = { x: -1, y: -1 }
const initialTransform = { scale: 1, xPos: 0, yPos: 0, pointerY: 0, pointerX: 0 }

export const ZoomableSlide: VFC<SliderProps> = ({ source, splide }) => {
  const { t } = useTranslation()
  const elementRef = useRef<any>(null)
  const [isZoomedIn, setIsZoomedIn] = useState(false)
  const [dragEnabled, setDragEnabled] = useState(false)
  const [pointer, setPointer] = useState<PointerPos>(initialPointer)
  const [transform, setTransform] = useState<TransformMatrix>(initialTransform)

  useEffect(() => {
    elementRef.current.style.transform = `scale(${transform.scale}) translate(${transform.xPos}px, ${transform.yPos}px)`
  }, [transform])

  const handleTransform = props => setTransform(prevState => ({
    ...prevState,
    ...props,
  }))

  /**
   * Calculates new position of image and updates the state accordingly
   * @param event The pointer event with the current position of touch/mouse.
   */
  const moveImage = useCallback((event) => {
    if (isZoomedIn && dragEnabled && pointer !== initialPointer) {
      const pointerX = event.clientX
      const pointerY = event.clientY

      /**
       * Calculation in all its beauty.
       * current transform position times the scale factor
       * minus the offset that it should be moved by (previous pointer
       * plus current) and everything is divided by the scale factor
       * again to represent the scaled image position
       */
      const xPos = (transform.xPos * SCALE_FACTOR - transform.pointerX + pointerX) / SCALE_FACTOR
      const yPos = (transform.yPos * SCALE_FACTOR - transform.pointerY + pointerY) / SCALE_FACTOR
      handleTransform({ xPos, yPos, pointerX, pointerY })
    }
  }, [dragEnabled, isZoomedIn, pointer, transform.pointerX, transform.pointerY, transform.xPos, transform.yPos])

  useEffect(() => {
    splide?.Components.Drag.disable(isZoomedIn)

    return () => {
      splide?.Components.Drag.disable(false)
    }
  }, [isZoomedIn, splide])

  const handleZoom = useCallback((event) => {
    // Apply a specific transition for a nicer zoom effect.
    applyZoomTransition(elementRef)
    elementRef.current.style.cursor = isZoomedIn ? 'pointer' : 'default'

    if (!isZoomedIn) {
      // Zoom into clicked/touched location
      const container = elementRef.current?.parentElement.getBoundingClientRect()
      const xPos = container.right / 2 - event.clientX
      const yPos = container.bottom / 2 - event.clientY
      handleTransform({ xPos, yPos, scale: SCALE_FACTOR })
    } else {
      // Zoom Out to initial state
      handleTransform(initialTransform)
      setPointer(initialPointer)
    }

    // Apply the default transition after zoom effect is done.
    setTimeout(() => applyTranslateTransistion(elementRef), 200)
    setIsZoomedIn(!isZoomedIn)
  }, [isZoomedIn])

  const handlePointerDown = useCallback((event) => {
    event.stopPropagation()
    setDragEnabled(true)

    const { clientX: x, clientY: y } = 'touches' in event
      ? event.touches[0]
      : event

    const pointerPosition = event.target.tagName !== 'IMG'
      ? initialPointer
      : { x, y }
    setPointer(pointerPosition)
    setTransform(prevState => ({ ...prevState, pointerX: x, pointerY: y }))
  }, [])

  const handlePointerUp = useCallback((event) => {
    event.stopPropagation()
    setDragEnabled(false)
    if (event.clientX === pointer.x && event.clientY === pointer.y) {
      handleZoom(event)
    }
  }, [handleZoom, pointer.x, pointer.y])

  const handleTouchMove = useCallback((event) => {
    const touch = event.touches[0]
    moveImage(touch)
  }, [moveImage])

  return (
    <>
      <ExpandFullWidthHeight
        onMouseDown={handlePointerDown}
        onMouseMove={moveImage}
        onMouseUp={handlePointerUp}
        onTouchEnd={handlePointerUp}
        onTouchMove={handleTouchMove}
        onTouchStart={handlePointerDown}
        ref={elementRef}
      >
        <ImageV2
          draggable={false}
          layout="fill"
          objectFit="contain"
          src={source}
        />
      </ExpandFullWidthHeight>
      {isZoomedIn && <ExitZoom onClick={handleZoom}>{t('common:ZOOM_OUT')}</ExitZoom>}
    </>

  )
}

